import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
} from "recharts";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  themeColors,
  backgroundColors,
  CustomDot,
  CustomActiveDot,
  ReferenceRanges,
  CustomTooltipTracker,
  TrendChartSummarySection,
} from "../../../HealthChart/components";
import {
  useChartSelection,
  useTrendChartCompactViewport,
} from "../../../HealthChart/hooks";
import {
  getChartFocusStyles,
  getTrendChartLayoutVitals,
  trendGraphCardHorizontalPaddingSx,
  trendGraphCardPaperSx,
} from "../../../HealthChart/utils";
import { ChartDataPoint } from "../../../HealthChart/types";

// Type alias for heart rate data points with required heartRate property
export type HeartRateDataPoint = ChartDataPoint &
  Required<Pick<ChartDataPoint, "heartRate">>;

export interface HeartRateChartProps {
  data: HeartRateDataPoint[];
}

import { getHeartRateColor } from "../../../HealthChart/utils/heartRateChartColors";
export { getHeartRateColor };

// -------- Main Chart --------
const HeartRateChart: React.FC<HeartRateChartProps> = ({ data }) => {
  const { t } = useTranslation();
  const isCompactChart = useTrendChartCompactViewport();
  const { right: chartRightMargin, yAxisWidth } = getTrendChartLayoutVitals(
    isCompactChart,
    27
  );

  // Use shared selection hook
  const {
    setHoveredPoint,
    hasAnimated,
    handlePointClick,
    activePoint,
    activeIndex,
  } = useChartSelection(data);

  if (!activePoint) {
    return null;
  }

  const yDomain: [number, number] = [30, 120];
  const yTicks = [50, 60, 90];

  const heartRateChartHeight = 200;
  const heartRateChartWidth = yAxisWidth;
  const yMargin = { top: 10, bottom: 10 };

  const referenceRanges = [
    {
      label: t("HealthChart.heartRateChart.zone.high"),
      yValue: 115,
      fontWeight: 700,
      color: themeColors.error,
    },
    {
      label: t("HealthChart.heartRateChart.zone.elevated"),
      yValue: 95,
      fontWeight: 700,
      color: themeColors.warning,
    },
    {
      label: t("HealthChart.heartRateChart.zone.normal"),
      yValue: 80,
      fontWeight: 700,
      color: themeColors.success,
    },
    {
      label: t("HealthChart.heartRateChart.zone.low"),
      yValue: 65,
      fontWeight: 700,
      color: themeColors.warning,
    },
  ];

  return (
    <>
      <style>{getChartFocusStyles("heart-rate-chart")}</style>
      <Box className="heart-rate-chart" sx={trendGraphCardPaperSx}>
        <Stack spacing={2} sx={trendGraphCardHorizontalPaddingSx}>
          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontSize: 16,
            }}
          >
            {t("HealthChart.heartRateChart.title")}
          </Typography>

          {/* Chart Container */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 200,
              outline: "none",
            }}
          >
            <ResponsiveContainer>
              <LineChart
                data={data}
                margin={{
                  top: 10,
                  right: chartRightMargin,
                  left: 0,
                  bottom: 14,
                }}
                onClick={handlePointClick}
              >
                {/* Grid */}
                <CartesianGrid
                  stroke={theme.palette.grey[300]}
                  strokeDasharray="5 5"
                  vertical={false}
                  horizontal={true}
                />

                <ReferenceArea
                  y1={90}
                  y2={yDomain[1]}
                  fill={backgroundColors.errorLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={61}
                  y2={90}
                  fill={backgroundColors.warningLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={50}
                  y2={60}
                  fill={backgroundColors.successLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={yDomain[0]}
                  y2={50}
                  fill={backgroundColors.warningLight}
                  fillOpacity={0.5}
                />
                <ReferenceLine
                  y={90}
                  stroke={themeColors.error}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
                <ReferenceLine
                  y={60}
                  stroke={themeColors.success}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
                <ReferenceLine
                  y={50}
                  stroke={themeColors.warning}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />

                {/* X Axis */}
                <XAxis
                  dataKey="date"
                  tick={{
                    fontSize: 10,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  dy={5}
                />

                <YAxis
                  domain={yDomain}
                  ticks={yTicks}
                  interval={0}
                  tick={{
                    fontSize: 11,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  width={yAxisWidth}
                />

                {/* Tooltip with custom cursor for vertical dashed line - placed before Line so it renders behind dots */}
                <Tooltip
                  content={(props) => (
                    <CustomTooltipTracker
                      {...props}
                      setHoveredPoint={setHoveredPoint}
                    />
                  )}
                  cursor={{
                    stroke: theme.palette.grey[900],
                    strokeWidth: 1,
                    strokeDasharray: "4 4",
                  }}
                />

                {/* Heart Rate Line */}
                <Line
                  type="linear"
                  dataKey="heartRate"
                  stroke={theme.palette.grey[300]}
                  strokeWidth={2}
                  dot={(props: any) => (
                    <CustomDot {...props} getColorFn={getHeartRateColor} />
                  )}
                  activeDot={(props: any) => (
                    <CustomActiveDot
                      {...props}
                      getColorFn={getHeartRateColor}
                    />
                  )}
                  isAnimationActive={hasAnimated.current}
                  animationDuration={800}
                  animationEasing="ease-in-out"
                />
              </LineChart>
            </ResponsiveContainer>

            {/* Reference Range Labels */}
            <ReferenceRanges
              yDomain={yDomain}
              chartHeight={heartRateChartHeight}
              yMargin={yMargin}
              ranges={referenceRanges}
            />
          </Box>

          <TrendChartSummarySection
            activeIndex={activeIndex}
            dataLength={data.length}
            chevronLeftMargin={heartRateChartWidth}
            chevronRightMargin={chartRightMargin}
            chevronOnlyWithMultipleDataPoints={false}
            datePrefix={t("HealthChart.heartRateChart.datePrefix")}
            fullDate={activePoint.fullDate}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color: theme.palette.grey[700],
                }}
              >
                {activePoint.heartRate}
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(14),
                  color: theme.palette.grey[900],
                  marginLeft: convertPxToRem(4),
                }}
              >
                {t("HealthChart.heartRateChart.unit")}
              </span>
            </div>
          </TrendChartSummarySection>
        </Stack>
      </Box>
    </>
  );
};

export default HeartRateChart;
